import React from 'react';
import { Alert, Button, Card, Spin, Typography } from 'antd';
import { GoogleOutlined } from '@ant-design/icons';
import { Echo } from '../components/Echo';
import { EnvBadge } from '../components/EnvBadge';

const { Title, Paragraph, Text } = Typography;

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <main
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
      background: '#f5f6fb',
    }}
  >
    <Card style={{ width: 420, maxWidth: '100%' }} styles={{ body: { padding: 32 } }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <EnvBadge />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        {children}
      </div>
    </Card>
  </main>
);

export const LoadingScreen: React.FC<{ label?: string }> = ({ label = 'Loading…' }) => (
  <Centered>
    <Echo mood="thinking" size={88} />
    <Spin />
    <Text type="secondary">{label}</Text>
  </Centered>
);

export const SignInScreen: React.FC<{ onSignIn: () => Promise<void>; error?: string }> = ({
  onSignIn,
  error,
}) => {
  const [busy, setBusy] = React.useState(false);
  return (
    <Centered>
      <Echo mood="hello" size={112} alt="Echo, the LingoPing bat" />
      <Title level={3} style={{ margin: 0 }}>
        LingoPing Admin
      </Title>
      <Paragraph type="secondary" style={{ textAlign: 'center', marginBottom: 16 }}>
        Sign in with the Google account that has admin access.
      </Paragraph>
      {error && <Alert type="error" showIcon title={error} style={{ width: '100%' }} />}
      <Button
        type="primary"
        size="large"
        icon={<GoogleOutlined />}
        loading={busy}
        block
        onClick={async () => {
          setBusy(true);
          try {
            await onSignIn();
          } finally {
            setBusy(false);
          }
        }}
      >
        Continue with Google
      </Button>
    </Centered>
  );
};

export const NoAccessScreen: React.FC<{ email: string; onSignOut: () => void }> = ({
  email,
  onSignOut,
}) => (
  <Centered>
    <Echo mood="sad" size={104} />
    <Title level={3} style={{ margin: 0, textAlign: 'center' }}>
      This account has no admin access
    </Title>
    <Paragraph type="secondary" style={{ textAlign: 'center', marginBottom: 16 }}>
      You are signed in as <Text strong>{email}</Text>. Ask the owner to add this account, or sign
      in with a different one.
    </Paragraph>
    <Button block size="large" onClick={onSignOut}>
      Sign out
    </Button>
  </Centered>
);

export const CheckErrorScreen: React.FC<{
  email: string;
  message: string;
  onRetry: () => void;
  onSignOut: () => void;
}> = ({ email, message, onRetry, onSignOut }) => (
  <Centered>
    <Echo mood="sad" size={104} />
    <Title level={4} style={{ margin: 0, textAlign: 'center' }}>
      Could not check admin access
    </Title>
    <Paragraph type="secondary" style={{ textAlign: 'center' }}>
      Signed in as {email}. {message}
    </Paragraph>
    <Button type="primary" block onClick={onRetry}>
      Try again
    </Button>
    <Button block onClick={onSignOut}>
      Sign out
    </Button>
  </Centered>
);

export const NotConfiguredScreen: React.FC = () => (
  <Centered>
    <Echo mood="sad" size={104} />
    <Title level={4} style={{ margin: 0 }}>
      No database configured
    </Title>
    <Paragraph type="secondary" style={{ textAlign: 'center' }}>
      This build has no <Text code>VITE_SUPABASE_URL</Text> /{' '}
      <Text code>VITE_SUPABASE_PUBLISHABLE_KEY</Text>. Set them in the repo-root{' '}
      <Text code>.env.&lt;mode&gt;</Text> file and build again.
    </Paragraph>
  </Centered>
);
